/**
 * QIGEmptyQPValidator class is responsible for validating if a Question Item Group (QIG) is empty.
 * It uses dependency injection to get necessary services and performs validation based on the model type.
 */
class QIGEmptyQPValidator {
    /**
     * Constructor for QIGEmptyQPValidator.
     * @param {Object} $injector - The injector object to get services.
     * @param {Object} model - The model object to be validated.
     */
    constructor($injector, model) {
        this.$injector = $injector;
        this.model = model;

        this._TaskDataService = null;
        this._translate = null;
        this._fetchContext = null;
        this._messageTranslateString = 'CMS.FACTORIES.MODELS.VALIDATORS.EMPTY.QIG-QP';
        this._message = null;
        this._forced_check = true;
    }

    /**
     * Get the TaskDataService instance.
     * @returns {Object} TaskDataService instance.
     */
    getTaskDataService() {
        if (!this._TaskDataService) {
            this._TaskDataService = this.$injector.get('TaskDataService');
        }
        return this._TaskDataService;
    }

    /**
     * Get the $translate service instance.
     * @returns {Object} $translate service instance.
     */
    getTranslateService() {
        if (!this._translate) {
            this._translate = this.$injector.get('$translate');
        }
        return this._translate;
    }

    /**
     * Get the fetchContext instance.
     * @returns {Object} fetchContext instance.
     */
    getFetchContext() {
        if (!this._fetchContext) {
            this._fetchContext = this.getTaskDataService().fetchContext();
        }
        return this._fetchContext;
    }

    /**
     * Validate the model based on its type.
     * @param {Object} from - The source object initiating the validation.
     * @returns {Object} Validation result.
     */
    validate(from) {
        let result = {};
        let error = false;

        if (this.model.getType() === 'QuestionItemGroup') {
            error = true;

            let question_parts = this.getFetchContext().findInstancesByType('questionPart');
            for (let qp_uuid in question_parts) {
                let question_part = question_parts[qp_uuid];
                if (this.model.getUUID() === question_part.data.questionItemGroup) {
                    error = false;
                    break;
                }
            }

            result = {
                'onSheetBlur': false,
                'onlyBanner': false,
                'validator': this.getValidatorName(),
                'modelUUID': this.model.getUUID(),
                'error': error,
                'status': error ? 'DANGER' : 'SUCCESS', // WARNING | DANGER | SUCCESS
                'message': this.getErrorMessage(),
                'message_template': false,
                'callbacks': []
            };

        } else {
            let question_item_groups = this.getFetchContext().findInstancesByType('QuestionItemGroup');

            for (let qig_uuid in question_item_groups) {
                let question_item_group = question_item_groups[qig_uuid];
                question_item_group.validate(this.model);
            }

            return result;
        }

        return result;
    }

    /**
     * Get the error message for the validation.
     * @returns {string} Error message.
     */
    getErrorMessage() {
        let questionHeadline = this.model.getQuestionHeadline();
        this._message = this.getTranslateService().instant(this._messageTranslateString, {
            s: this.model.getTitle()
        });

        return this._message;
    }

    /**
     * Check if the validation is forced.
     * @returns {boolean} True if the validation is forced, otherwise false.
     */
    isForcedCheck() {
        return this._forced_check;
    }

    /**
     * Get the name of the validator.
     * @returns {string} Validator name.
     */
    getValidatorName() {
        return 'QIGEmptyQPValidator';
    }
}

export default QIGEmptyQPValidator;